import { Select } from "@/components/ui/Select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@paribus/shared-ui";
import { Copy } from "lucide-react";
import React from "react";

import type { VisitsBulkCopyModalProps } from "./types";

export const VisitsBulkCopyModal: React.FC<VisitsBulkCopyModalProps> = ({
  selectedVisitCount,
  versions,
  currentVersionId,
  isSaving,
  onSave,
  onClose,
}) => {
  const [targetVersionId, setTargetVersionId] = React.useState("");

  const targetVersionOptions = versions
    .filter((version) => version.version_id !== currentVersionId)
    .map((version) => ({ value: version.version_id, label: version.displayName }));

  const handleOpenChange = (next: boolean) => {
    if (isSaving) return;
    if (!next) onClose();
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent
        size="sm"
        onInteractOutside={(e) => {
          if (isSaving) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isSaving) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-neutral-900">
            Copy visits to another version
          </DialogTitle>
          <DialogDescription className="text-sm text-neutral-500">
            Copy {selectedVisitCount} selected visit{selectedVisitCount !== 1 ? "s" : ""} into
            another version of this case.
          </DialogDescription>
        </DialogHeader>

        <div>
          <label
            htmlFor="bulk-copy-target-version"
            className="mb-1 block text-xs font-semibold text-neutral-600"
          >
            Target version
          </label>
          <Select
            id="bulk-copy-target-version"
            value={targetVersionId}
            onChange={setTargetVersionId}
            options={targetVersionOptions}
            placeholder="Select version"
            disabled={isSaving}
            className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus-visible:shadow-focus"
          />
        </div>

        <DialogFooter className="gap-2 sm:space-x-0">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void onSave(targetVersionId)}
            disabled={!targetVersionId}
            loading={isSaving}
            leadingIcon={<Copy aria-hidden />}
          >
            Copy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
